import type { Dispatch } from 'redux';
import { SubmissionError } from 'redux-form';

import { DefaultNotificationTimesFM } from 'af-root/scenes/Company/Settings/Notification/NotificationTimes/formModel';
import { AutomaticNotificationsFM } from 'af-root/scenes/Company/Settings/Notification/SpecialEmails/formModel';
import { ScheduleBoardSettingsRequestForm } from 'af-root/scenes/Company/Settings/ScheduleBoard/ScheduleBoardSettings/formModel';
import * as FieldReportSettingsFM from 'af-root/scenes/Company/Settings/WorkOrder/FieldReport/formModel';
import * as WorkOrderSettingsFM from 'af-root/scenes/Company/Settings/WorkOrder/WorkOrder/formModel';

import * as CreateCompanyAPI from 'ab-api/web/company/createCompany';
import * as EditCompanyAPI from 'ab-api/web/company/editCompany';
import * as EditCompanyPolicyAPI from 'ab-api/web/company/editCompanyPolicy';
import * as EditCredentialNotificationSettingAPI from 'ab-api/web/company/editCredentialNotificationSettings';
import * as EditInvoiceNotificationsPropertiesAPI from 'ab-api/web/company/editInvoiceNotifications';
import * as EditJobTrackingAPI from 'ab-api/web/company/editJobTracking';
import * as EditMaterialsEnabledAPI from 'ab-api/web/company/editMaterialsEnabled';
import * as EditPortalFunctionalitiesAPI from 'ab-api/web/company/editPortalFunctionalities';
import * as EditShiftSettingsAPI from 'ab-api/web/company/editShiftSettings';
import * as EditTimeRoundingPolicyAPI from 'ab-api/web/company/editTimeRoundingPolicyDate';
import * as EditWaiverSettingAPI from 'ab-api/web/company/editWaiverSetting';
import * as FindCompanyPolicyAPI from 'ab-api/web/company/findCompanyPolicy';
import * as OrganizationAPI from 'ab-api/web/organization/findAllMembers';
import * as PlatformAPI from 'ab-api/web/platform/findAllMembers';

import type { CompanyViewModel } from 'ab-viewModels/company.viewModel';
import type { MemberViewModel } from 'ab-viewModels/member.viewModel';
import type { NotificationSettingsViewModel } from 'ab-viewModels/notification.viewModel';
import type { OrganizationMemberVM } from 'ab-viewModels/organization/member.viewModel';
import type UserVM from 'ab-viewModels/user/purchaseOrderOption.viewModel';

import type { FieldReportSettingsRM } from 'ab-requestModels/company/fieldReportSettings.requestModel';
import type { WorkOrderSettingsRM } from 'ab-requestModels/company/workOrderSettings.requestModel';
import type { CompanyWorkDaysForm, CompanyPrintoutRM, EHSRM, EnableWorkRequestsRM, UpdatePurchaseOrderNumberPrefixRM } from 'ab-requestModels/company.requestModel';
import type { LaborSettingsRM } from 'ab-requestModels/employee.requestModel';
import type { EquipmentSettingsRM } from 'ab-requestModels/equipment.requestModel';
import type { GeneralNotifications, SpecialEmails, TemporaryLaborNotificationsRM, DefaultNotificationTimes } from 'ab-requestModels/notificationSettings.requestModel';
import type { AutomaticNotifications } from 'ab-requestModels/notificationSettings.requestModel';

import type { GetRootState } from 'af-reducers';

import type { ErrorOverride } from 'af-utils/actions.util';
import { errorHandler, defaultRedirectUrl } from 'af-utils/actions.util';
import * as FormUtil from 'af-utils/form.util';
import { http } from 'af-utils/http.util';

import { COMPANY_CREATE, COMPANY_EDIT } from 'af-constants/reduxForms';
import API from 'af-constants/routes/api';

import type { TableContent } from 'ab-common/dataStructures/tableContent';
import { TableQuery } from 'ab-common/dataStructures/tableQuery';

import type * as httpActions from 'af-reducers/http.reducer';

import * as authenticationActionCreators from 'af-actions/authentication/authentication.actionCreators';
import { _refreshUser } from 'af-actions/authentication/authentication.actions';
import * as companyActionCreators from 'af-actions/companies/companies.actionCreators';
import type * as generalActionCreators from 'af-actions/general/general.actionCreators';

// Public Functions

export function createCompany(form: CreateCompanyAPI.W_Company_CreateCompany_RM, orgAlias: string) {
	return async (
		dispatch: Dispatch<companyActionCreators.CompanyAction | authenticationActionCreators.AuthenticationAction | generalActionCreators.GeneralAction>,
		getState: GetRootState,
		{ redirectTo }
	) => {

		const action = async () => {
			const options = {
				submitting: COMPANY_CREATE,
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'multipart/form-data',
				},
			};
			const fd = FormUtil.getMultipartFormData(form, 'image');

			const { user, company } = await http.post<CreateCompanyAPI.W_Company_CreateCompany_VM>(CreateCompanyAPI.URL(), fd, options);

			dispatch(companyActionCreators.GET_COMPANY(company));
			const redirectUrl = defaultRedirectUrl(orgAlias, company.name, [], true, user.user.role);
			_refreshUser(user, company.name, dispatch, redirectTo, redirectUrl);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function deleteUser(userId: number) {
	return async (dispatch: Dispatch<companyActionCreators.CompanyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			await http.delete(API.V1.USERS.DELETE(userId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editCompany(id: number, request: EditCompanyAPI.W_Company_Edit_RM) {
	return async (
		dispatch: Dispatch<companyActionCreators.CompanyAction | authenticationActionCreators.UpdateCurrentCompany>,
		getState: GetRootState,
		{ redirectTo }
	) => {

		const action = async () => {
			const options = {
				submitting: COMPANY_EDIT,
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'multipart/form-data',
				},
			};
			const fd = FormUtil.getMultipartFormData(request, 'image');

			const response = await http.put<EditCompanyAPI.W_Company_Edit_VM>(EditCompanyAPI.URL(id), fd, options);
			dispatch(companyActionCreators.GET_COMPANY(response));
			dispatch(authenticationActionCreators.UPDATE_CURRENT_COMPANY(response));
		};

		const error: ErrorOverride = {
			err400: (e) => {
				if (e.response.data.abbreviation) {
					throw new SubmissionError({
						abbreviation: 'Company abbreviation is already in use.',
					});
				}

				if (e.response.data.name) {
					throw new SubmissionError({
						name: 'Company name is already in use.',
					});
				}

				throw new SubmissionError({
					_error: 'Unable to save company settings.',
				});
			},
		};

		return await errorHandler(action, dispatch, redirectTo, error);
	};
}

export function editPrintoutHeader(form: CompanyPrintoutRM) {
	return async (
		dispatch: Dispatch<companyActionCreators.CompanyAction | authenticationActionCreators.UpdateCurrentCompany>,
		getState: GetRootState, { redirectTo }
	) => {

		const action = async () => {
			const options = {
				submitting: COMPANY_EDIT,
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'multipart/form-data',
				},
			};
			const fd = FormUtil.getMultipartFormData(form, 'customPDFHeader');

			const response = await http.put<CompanyViewModel>(API.V1.COMPANY.EDIT_PRINTOUT, fd, options);
			dispatch(companyActionCreators.GET_COMPANY(response));
			dispatch(authenticationActionCreators.UPDATE_CURRENT_COMPANY(response));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editGeneralNotifications(form: GeneralNotifications) {
	return async (dispatch: Dispatch<companyActionCreators.CompanyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const notificationSettings = await http.put<GeneralNotifications>(API.V1.NOTIFY.EDIT_GENERAL, form);
			dispatch(companyActionCreators.GET_COMPANY_NOTIFICATION_SETTINGS(notificationSettings));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editDefaultNotificationSendTimes(form: DefaultNotificationTimes) {
	return async (dispatch: Dispatch<companyActionCreators.CompanyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const notificationSettings = await http.put<DefaultNotificationTimes>(API.V1.NOTIFY.EDIT_DEFAULT_TIMES, form);
			dispatch(companyActionCreators.GET_COMPANY_NOTIFICATION_SETTINGS(new DefaultNotificationTimesFM(notificationSettings)));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editSpecialEmailNotifications(form: SpecialEmails) {
	return async (dispatch: Dispatch<companyActionCreators.CompanyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const notificationSettings = await http.put<SpecialEmails>(API.V1.NOTIFY.EDIT_SPECIAL_EMAILS, form);
			dispatch(companyActionCreators.GET_COMPANY_NOTIFICATION_SETTINGS(notificationSettings as Partial<NotificationSettingsViewModel>));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editAutomaticNotifications(form: AutomaticNotifications) {
	return async (dispatch: Dispatch<companyActionCreators.CompanyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const requestForm: AutomaticNotifications = AutomaticNotificationsFM.fromForm(form);
			const notificationSettings = await http.put<AutomaticNotifications>(API.V1.NOTIFY.EDIT_AUTOMATIC, requestForm);
			dispatch(companyActionCreators.GET_COMPANY_NOTIFICATION_SETTINGS(notificationSettings));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editTemporaryLaborNotifications(form: TemporaryLaborNotificationsRM) {
	return async (dispatch: Dispatch<companyActionCreators.CompanyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const notificationSettings = await http.put<NotificationSettingsViewModel>(API.V1.NOTIFY.EDIT_TEMPORARY_LABOR, form);
			dispatch(companyActionCreators.GET_COMPANY_NOTIFICATION_SETTINGS(notificationSettings));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editCredentialNotificationSettings(request: EditCredentialNotificationSettingAPI.W_Company_EditCredentialNotificationSettings_RM) {
	return async (dispatch: Dispatch<companyActionCreators.CompanyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const response = await http.put<EditCredentialNotificationSettingAPI.W_Company_EditCredentialNotificationSettings_VM>(
				EditCredentialNotificationSettingAPI.URL(),
				request
			);
			dispatch(companyActionCreators.GET_COMPANY(response));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editCompanyWorkDays(form: CompanyWorkDaysForm) {
	return async (dispatch: Dispatch<companyActionCreators.CompanyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const response: CompanyViewModel = await http.put(API.V1.COMPANY.EDIT_WORK_DAYS, form);
			dispatch(companyActionCreators.GET_COMPANY(response));
		};
		const error: ErrorOverride = {
			err400: () => {
				throw new SubmissionError({ _error: 'Company name is already in use.' });
			},
		};
		return await errorHandler(action, dispatch, redirectTo, error);
	};
}

export function editScheduleBoardSettings(form: ScheduleBoardSettingsRequestForm) {
	return async (dispatch: Dispatch<companyActionCreators.CompanyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			const response = await http.put<CompanyViewModel>(API.V1.COMPANY.EDIT_SCHEDULE_BOARD_SETTINGS, ScheduleBoardSettingsRequestForm.toModel(form));
			dispatch(companyActionCreators.GET_COMPANY(response));
			return response;
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editWorkOrderSettings(form: WorkOrderSettingsRM) {
	return async (dispatch: Dispatch<companyActionCreators.CompanyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			const response = await http.put<CompanyViewModel>(API.V1.COMPANY.EDIT_WORK_ORDER_SETTINGS, WorkOrderSettingsFM.fromFMtoRM(form));
			dispatch(companyActionCreators.GET_COMPANY(response));
			return response;
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editFieldReportSettings(form: FieldReportSettingsRM) {
	return async (dispatch: Dispatch<companyActionCreators.CompanyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			const response = await http.put<CompanyViewModel>(API.V1.COMPANY.EDIT_FIELD_REPORT_SETTINGS, FieldReportSettingsFM.fromFMtoRM(form));
			dispatch(companyActionCreators.GET_COMPANY(response));
			return response;
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function getCompany() {
	return async (dispatch: Dispatch<companyActionCreators.CompanyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const company = await http.get<CompanyViewModel>(API.V1.COMPANY.FIND_BY_ID);
			dispatch(companyActionCreators.GET_COMPANY(company));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function getMembersForOrganizationByAlias(tableRequestModel: TableQuery = {} as TableQuery, alias: string) {
	return async (dispatch: Dispatch<companyActionCreators.CompanyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			const fullName = tableRequestModel.sortBy?.find((s) => s.id === 'fullName');
			if (fullName) {
				tableRequestModel.sortBy = (tableRequestModel.sortBy ?? []).filter((_s) => _s.id !== 'fullName').concat(
					{ id: 'lastName', desc: fullName.desc },
					{ id: 'firstName', desc: fullName.desc }
				);
			}

			const data = new TableQuery(tableRequestModel);
			return await http.get<TableContent<MemberViewModel>>(PlatformAPI.URL(data, alias));
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function removeOrganizationAdminPrivileges(userId: string, alias: string) {
	return async (dispatch: Dispatch<companyActionCreators.CompanyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			await http.put(API.V1.ORGANIZATION.USERS.UPDATE_ADMIN_PRIVILEGES(userId, alias, false));
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function giveOrganizationAdminPrivileges(userId: string, alias: string) {
	return async (dispatch: Dispatch<companyActionCreators.CompanyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			await http.put(API.V1.ORGANIZATION.USERS.UPDATE_ADMIN_PRIVILEGES(userId, alias, true));
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function getMembersForOrganization(tableRequestModel: TableQuery = {} as TableQuery) {
	return async (dispatch: Dispatch<companyActionCreators.CompanyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const fullName = tableRequestModel.sortBy?.find((s) => s.id === 'fullName');
			if (fullName) {
				tableRequestModel.sortBy = (tableRequestModel.sortBy ?? []).filter((_s) => _s.id !== 'fullName').concat(
					{ id: 'lastName', desc: fullName.desc },
					{ id: 'firstName', desc: fullName.desc }
				);
			}

			const data = new TableQuery(tableRequestModel);
			return await http.get<TableContent<OrganizationMemberVM>>(OrganizationAPI.URL(data));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editEquipmentSettings(form: EquipmentSettingsRM) {
	return async (dispatch: Dispatch<companyActionCreators.CompanyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			const response = await http.patch<CompanyViewModel>(API.V1.COMPANY.EDIT_EQUIPMENT_SETTINGS, form);
			dispatch(companyActionCreators.GET_COMPANY(response));
			return response;
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editLaborSettings(form: LaborSettingsRM) {
	return async (dispatch: Dispatch<companyActionCreators.CompanyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			const response = await http.patch<CompanyViewModel>(API.V1.COMPANY.EDIT_LABOR_SETTINGS, form);
			dispatch(companyActionCreators.GET_COMPANY(response));
			return response;
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editEHS(form: EHSRM) {
	return async (dispatch: Dispatch<companyActionCreators.CompanyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			const response = await http.put<CompanyViewModel>(API.V1.COMPANY.EDIT_EHS, form);
			dispatch(companyActionCreators.GET_COMPANY(response));
			return response;
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editEnableWorkRequests(form: EnableWorkRequestsRM) {
	return async (
		dispatch: Dispatch<companyActionCreators.CompanyAction | authenticationActionCreators.UpdateCurrentCompany>,
		getState: GetRootState, { redirectTo }
	) => {
		const action = async () => {
			const response = await http.put<CompanyViewModel>(API.V1.COMPANY.EDIT_ENABLE_WORK_REQUESTS, form);
			dispatch(companyActionCreators.GET_COMPANY(response));
			dispatch(authenticationActionCreators.UPDATE_CURRENT_COMPANY(response));
			return response;
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editEnablePortalFunctionalities(form: EditPortalFunctionalitiesAPI.W_Company_EditPortalFunctionalities_RM) {
	return async (
		dispatch: Dispatch<companyActionCreators.CompanyAction | authenticationActionCreators.UpdateCurrentCompany>,
		getState: GetRootState, { redirectTo }
	) => {
		const action = async () => {
			const response = await http.put<EditPortalFunctionalitiesAPI.W_Company_EditPortalFunctionalities_VM>(
				EditPortalFunctionalitiesAPI.URL(),
				form
			);
			dispatch(companyActionCreators.GET_COMPANY(response));
			dispatch(authenticationActionCreators.UPDATE_CURRENT_COMPANY(response));
			return response;
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editMaterialsEnabled(form: EditMaterialsEnabledAPI.W_Company_EditMaterialsEnabled_RM) {
	return async (
		dispatch: Dispatch<companyActionCreators.CompanyAction | authenticationActionCreators.UpdateCurrentCompany>,
		getState: GetRootState, { redirectTo }
	) => {
		const action = async () => {
			const response = await http.put<EditMaterialsEnabledAPI.W_Company_EditMaterialsEnabled_VM>(
				EditMaterialsEnabledAPI.URL(),
				form
			);
			dispatch(companyActionCreators.GET_COMPANY(response));
			dispatch(authenticationActionCreators.UPDATE_CURRENT_COMPANY(response));
			return response;
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editJobTracking(form: EditJobTrackingAPI.W_Company_EditJobTracking_RM) {
	return async (
		dispatch: Dispatch<companyActionCreators.CompanyAction | authenticationActionCreators.UpdateCurrentCompany>,
		getState: GetRootState, { redirectTo }
	) => {
		const action = async () => {
			const response = await http.put<EditJobTrackingAPI.W_Company_EditJobTracking_VM>(
				EditJobTrackingAPI.URL(),
				form
			);
			dispatch(companyActionCreators.GET_COMPANY(response));
			dispatch(authenticationActionCreators.UPDATE_CURRENT_COMPANY(response));
			return response;
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editEnableInvoiceNotifications(form: EditInvoiceNotificationsPropertiesAPI.W_Company_EditInvoiceNotificationsProperties_RM) {
	return async (
		dispatch: Dispatch<companyActionCreators.CompanyAction | authenticationActionCreators.UpdateCurrentCompany>,
		getState: GetRootState, { redirectTo }
	) => {
		const action = async () => {
			const options = {
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'multipart/form-data',
				},
			};
			const fd = FormUtil.getMultipartFormData(form, 'image');

			const response = await http.put<EditInvoiceNotificationsPropertiesAPI.W_Company_EditInvoiceNotificationsProperties_VM>(
				EditInvoiceNotificationsPropertiesAPI.URL(),
				fd,
				options
			);
			dispatch(companyActionCreators.GET_COMPANY(response));
			dispatch(authenticationActionCreators.UPDATE_CURRENT_COMPANY(response));
			return response;
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function updatePurchaseOrderNumberPrefix(form: UpdatePurchaseOrderNumberPrefixRM) {
	return async (
		dispatch: Dispatch<companyActionCreators.CompanyAction | authenticationActionCreators.UpdateCurrentCompany>,
		getState: GetRootState, { redirectTo }
	) => {
		const action = async () => {
			const response = await http.put<CompanyViewModel>(API.V1.COMPANY.UPDATE_PURCHASE_ORDER_NUMBER_PREFIX, form);
			dispatch(companyActionCreators.GET_COMPANY(response));
			dispatch(authenticationActionCreators.UPDATE_CURRENT_COMPANY(response));
			return response;
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findCompanyUsers() {
	return async (
		dispatch: Dispatch<companyActionCreators.CompanyAction>,
		getState: GetRootState, { redirectTo }
	) => {
		const action = async () => {
			const response = await http.get<UserVM[]>(API.V1.COMPANY.FIND_USERS);
			return response;
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editNewTimeRoundingPolicyDate(form: EditTimeRoundingPolicyAPI.W_Company_EditTimeRoundingPolicy_RM) {
	return async (
		dispatch: Dispatch<companyActionCreators.CompanyAction | authenticationActionCreators.UpdateCurrentCompany>,
		getState: GetRootState, { redirectTo }
	) => {
		const action = async () => {
			const response = await http.put<EditTimeRoundingPolicyAPI.W_Company_EditTimeRoundingPolicy_VM>(
				EditTimeRoundingPolicyAPI.URL(),
				form
			);
			dispatch(companyActionCreators.GET_COMPANY(response));
			dispatch(authenticationActionCreators.UPDATE_CURRENT_COMPANY(response));
			return response;
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editShiftSettings(form: EditShiftSettingsAPI.W_Company_EditShiftSettings_RM) {
	return async (
		dispatch: Dispatch<companyActionCreators.CompanyAction | authenticationActionCreators.UpdateCurrentCompany>,
		getState: GetRootState, { redirectTo }
	) => {
		const action = async () => {
			const response = await http.patch<EditShiftSettingsAPI.W_Company_EditShiftSettings_VM>(
				EditShiftSettingsAPI.URL(),
				form
			);

			dispatch(companyActionCreators.GET_COMPANY(response));
			dispatch(authenticationActionCreators.UPDATE_CURRENT_COMPANY(response));
			return response;
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findCompanyPolicy() {
	return async (
		dispatch: Dispatch<companyActionCreators.CompanyAction | authenticationActionCreators.UpdateCurrentCompany>,
		getState: GetRootState, { redirectTo }
	) => {
		const action = async () => {
			return await http.get<FindCompanyPolicyAPI.W_Company_FindCompanyPolicy_VM_Serialized>(FindCompanyPolicyAPI.URL());
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editCompanyPolicy(request: EditCompanyPolicyAPI.W_Web_EditCompanyPolicy_RM) {
	return async (
		dispatch: Dispatch<
			companyActionCreators.CompanyAction |
			authenticationActionCreators.UpdateCurrentCompany |
			httpActions.UploadProgress |
			httpActions.UploadProgressSize
		>,
		getState: GetRootState, { redirectTo }
	) => {
		const { files, deleteFile } = request;
		const action = async () => {
			const options = {
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			};

			const fd = new FormData();
			if (!deleteFile) {
				files.forEach((_file) => fd.append('files[]', _file));
			}
			fd.append('data', JSON.stringify({ deleteFile }));

			await http.post(EditCompanyPolicyAPI.URL(), fd, options);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editWaiverSetting(form: EditWaiverSettingAPI.W_Company_EditWaiverSetting_RM) {
	return async (
		dispatch: Dispatch<companyActionCreators.CompanyAction | authenticationActionCreators.UpdateCurrentCompany>,
		getState: GetRootState, { redirectTo }
	) => {
		const action = async () => {
			const response = await http.put<EditWaiverSettingAPI.W_Company_EditWaiverSetting_VM>(
				EditWaiverSettingAPI.URL(),
				form
			);
			dispatch(companyActionCreators.GET_COMPANY(response));
			dispatch(authenticationActionCreators.UPDATE_CURRENT_COMPANY(response));
			return response;
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}
